import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CheckCircle2, ChevronDown, Mail, Plus, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import type { components } from '@/api/schema'
import {
  useCreateOrderLinePurchaseOrder,
  useManufacturer,
  useOrder,
  useUpdateOrderLineAction,
} from '@/api/hooks'
import { LoadingButton } from '@/components/loading-button'
import {
  ManufacturerAssignmentPicker,
  type ManufacturerAssignment,
} from '@/components/manufacturer-assignment-picker'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/search-select'
import { getApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import { ComposeTextarea } from '@/components/compose-field'

type ProjectAction = components['schemas']['ProjectOpenActionRead']
type OrderLineAction = components['schemas']['OrderLineActionRead']
type Disposition = 'create_po' | 'existing_po' | 'manual_po' | 'other'

const DISPOSITIONS = [
  { value: 'create_po', label: 'Create new PO' },
  { value: 'existing_po', label: 'Already on a PO' },
  { value: 'manual_po', label: 'Ordered outside dashboard' },
  { value: 'other', label: 'Resolve with notes' },
] as const

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

function positiveNet(value: string | null | undefined) {
  return value != null && Number(value) > 0 ? value : null
}

function namesManufacturer(title: string, manufacturer: ManufacturerAssignment) {
  const text = title.toLowerCase()
  return [manufacturer.name, manufacturer.short_name].some(
    (name) => name != null && name.trim().length > 1 && text.includes(name.trim().toLowerCase()),
  )
}

function poRecipient(manufacturer: components['schemas']['ManufacturerDetailRead'] | undefined) {
  const links = manufacturer?.contact_links ?? []
  return (
    links.find((link) => link.role === 'po_primary') ??
    links.find((link) => link.role === 'purchasing') ??
    links.find((link) => link.role === 'primary') ??
    links.find(
      (link) =>
        !['spec_credit', 'spec_credit_cc', 'spec_credit_backup', 'spec_credit_backup_cc'].includes(link.role),
    )
  )?.contact
}

export function OrderLineActionResolution({
  action,
  orderUuid: orderUuidProp,
}: {
  action: ProjectAction | OrderLineAction
  orderUuid?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [disposition, setDisposition] = useState<Disposition | ''>('')
  const [purchaseOrderUuid, setPurchaseOrderUuid] = useState('')
  const [manualPoNumber, setManualPoNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [manufacturer, setManufacturer] = useState<ManufacturerAssignment | null>(null)
  const [useCustomManufacturer, setUseCustomManufacturer] = useState(false)
  const [customManufacturerName, setCustomManufacturerName] = useState('')
  const [customManufacturerEmail, setCustomManufacturerEmail] = useState('')
  const [netPrice, setNetPrice] = useState('')
  const [addedLines, setAddedLines] = useState<Record<number, string>>({})
  const [createdPurchaseOrder, setCreatedPurchaseOrder] = useState<{
    uuid: string
    poNumber: string
  } | null>(null)
  const orderUuid = orderUuidProp ?? ('order_uuid' in action ? action.order_uuid : '') ?? ''
  const { data: order } = useOrder(isOpen ? orderUuid : '')
  const updateMutation = useUpdateOrderLineAction(orderUuid)
  const createMutation = useCreateOrderLinePurchaseOrder(orderUuid)
  const manufacturerDetail = useManufacturer(manufacturer?.uuid ?? '')
  const recipient = poRecipient(manufacturerDetail.data)
  const line = order?.line_items?.find((item) => item.id === action.line_item_id)
  const storedNet = positiveNet(line?.cost_price)
  const otherLines = (order?.line_actions ?? []).filter(
    (other) =>
      other.id !== action.id &&
      other.status === 'open' &&
      other.reason_code !== 'warranty' &&
      other.line_item.product_id == null &&
      other.line_item.purchase_order_id == null,
  )
  const addedEntries = otherLines.filter((other) => other.id in addedLines)
  const addedNetMissing = addedEntries.some(
    (other) => !positiveNet(other.line_item.cost_price) && !(Number(addedLines[other.id]) > 0),
  )
  const hasCustomManufacturer = Boolean(
    customManufacturerName.trim() && EMAIL_PATTERN.test(customManufacturerEmail.trim()),
  )
  const canCreatePO = Boolean(line && line.product_id == null && action.reason_code !== 'warranty')
  const dispositions = canCreatePO
    ? DISPOSITIONS
    : DISPOSITIONS.filter((option) => option.value !== 'create_po')

  const submit = () => {
    if (disposition === 'create_po') {
      if ((!manufacturer && !hasCustomManufacturer) || (!storedNet && !netPrice)) return
      const additionalLines = addedEntries.map((other) => ({
        action_id: other.id,
        net_price: positiveNet(other.line_item.cost_price) ? null : Number(addedLines[other.id]),
      }))
      createMutation.mutate(
        {
          actionId: action.id,
          body: manufacturer
            ? {
                manufacturer_id: manufacturer.id,
                net_price: storedNet ? null : Number(netPrice),
                ...(additionalLines.length > 0 && { additional_lines: additionalLines }),
              }
            : {
                manufacturer_name: customManufacturerName.trim(),
                manufacturer_email: customManufacturerEmail.trim(),
                net_price: storedNet ? null : Number(netPrice),
                ...(additionalLines.length > 0 && { additional_lines: additionalLines }),
              },
        },
        {
          onSuccess: (result) => {
            setCreatedPurchaseOrder({
              uuid: result.purchase_order_uuid,
              poNumber: result.po_number,
            })
            const outcome =
              result.routing_decision === 'send_po_email'
                ? 'PO email queued'
                : result.routing_decision === 'send_spec_credit_email'
                  ? 'Spec credit request queued'
                  : 'PO held for review'
            toast.success(`${result.po_number} created. ${outcome}.`)
          },
          onError: (error) =>
            toast.error(getApiErrorMessage(error, 'Could not create this purchase order')),
        },
      )
      return
    }
    if (!disposition || !notes.trim()) return
    updateMutation.mutate(
      {
        actionId: action.id,
        body: {
          status: 'resolved',
          disposition,
          notes: notes.trim(),
          purchase_order_uuid: disposition === 'existing_po' ? purchaseOrderUuid || null : null,
          manual_po_number: disposition === 'manual_po' ? manualPoNumber.trim() || null : null,
        },
      },
      {
        onSuccess: () => toast.success('Resolution recorded'),
        onError: (error) => toast.error(getApiErrorMessage(error, 'Could not update this action')),
      },
    )
  }

  if (createdPurchaseOrder) {
    return (
      <Button variant="outline" size="sm" asChild>
        <Link to="/purchase-orders/$poId" params={{ poId: createdPurchaseOrder.uuid }}>
          View {createdPurchaseOrder.poNumber}
        </Link>
      </Button>
    )
  }

  if (!isOpen) {
    return (
      <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
        Resolve action
        <ChevronDown />
      </Button>
    )
  }

  return (
    <div className="space-y-3 border-t border-orange-200 pt-3">
      <ReviewChoiceGroup
        label="How should this line be handled?"
        value={disposition || null}
        options={dispositions}
        onChange={setDisposition}
      />

      {disposition && disposition !== 'create_po' && (
        <p className="text-sm text-foreground">
          Records this resolution only. No items are added and no PO is sent.
        </p>
      )}

      {disposition === 'create_po' && (
        <div className="space-y-3 rounded-lg border bg-background p-3">
          <div className="space-y-1">
            <Label>Manufacturer</Label>
            {useCustomManufacturer ? (
              <div className="space-y-3 rounded-md border p-3">
                <div className="space-y-1">
                  <Label htmlFor={`manufacturer-name-${action.id}`}>Manufacturer name</Label>
                  <InlineFieldInput
                    id={`manufacturer-name-${action.id}`}
                    value={customManufacturerName}
                    onChange={(event) => setCustomManufacturerName(event.target.value)}
                    placeholder="Manufacturer name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`manufacturer-email-${action.id}`}>PO email</Label>
                  <InlineFieldInput
                    id={`manufacturer-email-${action.id}`}
                    type="email"
                    value={customManufacturerEmail}
                    onChange={(event) => setCustomManufacturerEmail(event.target.value)}
                    placeholder="orders@manufacturer.com"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseCustomManufacturer(false)}
                >
                  Choose an existing manufacturer
                </Button>
              </div>
            ) : (
              <>
                <ManufacturerAssignmentPicker
                  currentId={manufacturer?.id}
                  currentName={manufacturer?.name}
                  onChange={(next) => {
                    setManufacturer(next)
                    setAddedLines(
                      Object.fromEntries(
                        otherLines
                          .filter((other) => namesManufacturer(other.line_item.title, next))
                          .map((other) => [other.id, addedLines[other.id] ?? '']),
                      ),
                    )
                  }}
                  className="w-full justify-start border bg-background px-3 py-2 font-normal"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setManufacturer(null)
                    setUseCustomManufacturer(true)
                  }}
                >
                  <Plus /> Manufacturer not listed
                </Button>
              </>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor={`supplier-net-${action.id}`}>Supplier Net</Label>
            {storedNet ? (
              <p id={`supplier-net-${action.id}`} className="text-sm font-medium tabular-nums">
                {formatMoney(storedNet)}
              </p>
            ) : (
              <InlineFieldInput
                id={`supplier-net-${action.id}`}
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                value={netPrice}
                onChange={(event) => setNetPrice(event.target.value)}
                placeholder="$0.00"
              />
            )}
          </div>

          {manufacturer && !useCustomManufacturer && (
            <div className="flex items-start gap-2 text-sm">
              <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {manufacturerDetail.isLoading ? (
                <span className="text-muted-foreground">Checking PO recipient...</span>
              ) : manufacturer.skip_po_email ? (
                <span className="text-destructive">Automatic PO email is disabled.</span>
              ) : recipient ? (
                <span className="min-w-0 break-all">
                  To {recipient.name ? `${recipient.name} · ` : ''}
                  {recipient.email}
                </span>
              ) : (
                <Link
                  to="/manufacturers/$manuId"
                  params={{ manuId: manufacturer.uuid }}
                  className="text-primary hover:underline"
                >
                  Add a PO recipient
                </Link>
              )}
            </div>
          )}

          {otherLines.length > 0 && (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">Also on this PO</legend>
              {otherLines.map((other) => {
                const otherNet = positiveNet(other.line_item.cost_price)
                const checked = other.id in addedLines
                return (
                  <div key={other.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 shrink-0 cursor-pointer accent-primary"
                        checked={checked}
                        onChange={(event) =>
                          setAddedLines((current) => {
                            const next = { ...current }
                            if (event.target.checked) next[other.id] = ''
                            else delete next[other.id]
                            return next
                          })
                        }
                      />
                      <span className="min-w-0">
                        <span className="block break-words">{other.line_item.title}</span>
                        <span className="block text-xs text-muted-foreground">
                          Qty {other.line_item.quantity}
                          {other.line_item.sku ? ` · ${other.line_item.sku}` : ''}
                          {otherNet ? ` · Net ${formatMoney(otherNet)}` : ''}
                        </span>
                      </span>
                    </label>
                    {!otherNet && checked && (
                      <InlineFieldInput
                        type="number"
                        min="0.01"
                        step="0.01"
                        inputMode="decimal"
                        className="w-28"
                        aria-label={`Supplier Net for ${other.line_item.title}`}
                        value={addedLines[other.id]}
                        onChange={(event) =>
                          setAddedLines((current) => ({
                            ...current,
                            [other.id]: event.target.value,
                          }))
                        }
                        placeholder="Net"
                      />
                    )}
                  </div>
                )
              })}
            </fieldset>
          )}
        </div>
      )}

      {disposition === 'existing_po' && (
        <div className="space-y-1">
          <Label htmlFor={`handled-po-${action.id}`}>PO already containing this item</Label>
          <SearchSelect
            id={`handled-po-${action.id}`}
            className="h-9 w-full"
            value={purchaseOrderUuid || undefined}
            options={(order?.purchase_orders ?? []).map((po) => ({ value: po.uuid, label: po.po_number }))}
            onChange={(value) => value && setPurchaseOrderUuid(value)}
            placeholder="Choose purchase order"
            searchPlaceholder="Search purchase orders"
          />
        </div>
      )}

      {disposition === 'manual_po' && (
        <div className="space-y-1">
          <Label htmlFor={`external-po-${action.id}`}>External PO number</Label>
          <InlineFieldInput
            id={`external-po-${action.id}`}
            value={manualPoNumber}
            onChange={(event) => setManualPoNumber(event.target.value)}
          />
        </div>
      )}

      {disposition !== 'create_po' && (
        <ComposeTextarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Resolution notes"
          aria-label="Resolution notes"
        />
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <LoadingButton
          size="sm"
          loading={updateMutation.isPending || createMutation.isPending}
          loadingText={disposition === 'create_po' ? 'Creating...' : 'Saving...'}
          disabled={
            !disposition ||
            (disposition !== 'create_po' && !notes.trim()) ||
            (disposition === 'existing_po' && !purchaseOrderUuid) ||
            (disposition === 'manual_po' && !manualPoNumber.trim()) ||
            (disposition === 'create_po' &&
              ((!manufacturer && !hasCustomManufacturer) ||
                (!storedNet && (!netPrice || Number(netPrice) <= 0)) ||
                addedNetMissing ||
                (manufacturer != null &&
                  (manufacturerDetail.isLoading || manufacturer.skip_po_email || !recipient))))
          }
          onClick={submit}
        >
          <CheckCircle2 />
          {disposition === 'create_po' ? 'Create new PO' : 'Save resolution'}
        </LoadingButton>
      </div>
    </div>
  )
}

export function ReopenOrderLineAction({
  action,
  orderUuid,
}: {
  action: OrderLineAction
  orderUuid: string
}) {
  const mutation = useUpdateOrderLineAction(orderUuid)
  return (
    <LoadingButton
      variant="outline"
      size="sm"
      loading={mutation.isPending}
      onClick={() =>
        mutation.mutate(
          { actionId: action.id, body: { status: 'open' } },
          {
            onSuccess: () => toast.success('Action reopened'),
            onError: () => toast.error('Could not reopen this action'),
          },
        )
      }
    >
      <RotateCcw />
      Reopen
    </LoadingButton>
  )
}
